'use client';

import { formatPhoneFromParts } from '@pickleballinc/lib/phone-utils';
import { forwardRef } from 'react';

import { TeamPlayerInfo, ActivityPlayerInfoData, TeamLeaguePlayerStatus } from '@/entities/TeamPlayerInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { PlayerRole } from '@/lib/types';
import { getPlayerStatusInfo } from '@/utils/helpers';

interface TeamRosterPrintProps {
	teamName: string;
	leagueInfo: {
		seasonTitle?: string;
		title?: string;
	};
	players: (TeamPlayerInfo | ActivityPlayerInfoData)[];
}

const formatRating = (singles: number | null, doubles: number | null) => {
	const singlesStr = singles ? singles.toFixed(2) : 'NR';
	const doublesStr = doubles ? doubles.toFixed(2) : 'NR';
	return {
		singles: `S: ${singlesStr}`,
		doubles: `D: ${doublesStr}`
	};
};

const formatPayment = (payment: number): string => {
	return `$${(payment / 100).toFixed(2)}`;
};

const getRoleLabel = (status?: string | TeamLeaguePlayerStatus): string => {
	if (!status) return 'Player';
	const statusValue = typeof status === 'string' ? status : status;
	const { label } = getPlayerStatusInfo(statusValue as TeamLeaguePlayerStatus);
	return label || 'Player';
};

const getInjuredStatus = (player: TeamPlayerInfo | ActivityPlayerInfoData): boolean => {
	if ('isInjured' in player) {
		return player.isInjured;
	}
	return false;
};

const getStatusLabel = (isInjured?: boolean): string => {
	if (isInjured) {
		return 'Injured Reserve';
	}
	return 'Active';
};

export const TeamRosterPrint = forwardRef<HTMLDivElement, TeamRosterPrintProps>(({ teamName, leagueInfo, players }, ref) => {
	const teamLeagueInfo = useTeamLeagueInfo();

	// Sort players: captains first, then co-captains, then by name
	const statusSortOrder: Record<PlayerRole, number> = {
		[PlayerRole.TEAM_LEAGUE_TEAM_CAPTAIN]: 1,
		[PlayerRole.TEAM_LEAGUE_TEAM_CAPTAIN_ASSISTANCE]: 2,
		[PlayerRole.TEAM_LEAGUE_TEAM_PLAYER]: 3
	};

	const sortedPlayers = [...players].sort((a, b) => {
		// First sort by role (captain, co-captain, player)
		const aRole = a.status as PlayerRole;
		const bRole = b.status as PlayerRole;
		const aRoleOrder = statusSortOrder[aRole] ?? 99;
		const bRoleOrder = statusSortOrder[bRole] ?? 99;

		if (aRoleOrder !== bRoleOrder) {
			return aRoleOrder - bRoleOrder;
		}

		// If same role, sort alphabetically by name
		return a.fullname?.localeCompare(b.fullname);
	});

	const currentDateTime = new Date().toLocaleString();

	return (
		<div ref={ref} className="print-container">
			{/* Print-specific styles */}
			<style jsx>{`
				@media print {
					.print-container {
						font-size: 11px;
						line-height: 1.4;
						color: black;
						background: white;
					}

					.print-header {
						position: sticky;
						top: 0;
						background: white;
						border-bottom: 2px solid black;
						margin-bottom: 16px;
						padding-bottom: 12px;
					}

					.print-table {
						width: 100%;
						border-collapse: collapse;
						margin-top: 8px;
					}

					.print-table th,
					.print-table td {
						border: 1px solid #333;
						padding: 6px 4px;
						text-align: left;
						font-size: 11px;
					}

					.print-table th {
						background-color: #f5f5f5;
						font-weight: bold;
						font-size: 10px;
					}

					.print-table tr {
						break-inside: avoid;
						page-break-inside: avoid;
					}

					.print-table tbody tr:nth-child(even) {
						background-color: #f9f9f9;
					}

					.print-controls {
						display: none !important;
					}

					@page {
						margin: 12mm 15mm;
						size: A4;
					}
				}

				/* Screen styles */
				@media screen {
					.print-container {
						max-width: 210mm;
						margin: 0 auto;
						padding: 20px;
						background: white;
						box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
						font-size: 12px;
					}

					.print-table {
						width: 100%;
						border-collapse: collapse;
						margin-top: 16px;
					}

					.print-table th,
					.print-table td {
						border: 1px solid #ddd;
						padding: 8px 6px;
						text-align: left;
					}

					.print-table th {
						background-color: #f8f9fa;
						font-weight: 600;
					}

					.print-table tbody tr:nth-child(even) {
						background-color: #f8f9fa;
					}
				}
			`}</style>

			{/* Header */}
			<div className="print-header text-center">
				<h1 className="text-xl font-bold mb-2">{teamName}</h1>
				<p className="text-sm text-gray-600 mb-1">
					{leagueInfo.title}
					{leagueInfo.seasonTitle ? ` — ${leagueInfo.seasonTitle}` : ''}
				</p>
				<p className="text-xs text-gray-500">Printed: {currentDateTime}</p>
			</div>

			{/* Roster Table */}
			<table className="print-table">
				<thead>
					<tr>
						<th style={{ width: '5%' }}>#</th>
						<th style={{ width: '20%' }}>Player</th>
						<th style={{ width: '15%' }}>Rating</th>
						<th style={{ width: '15%' }}>Role</th>
						<th style={{ width: '13%' }}>Status</th>
						{teamLeagueInfo.useLineupStrength ? <th style={{ width: '12%' }}>Lineup Strength</th> : null}
						<th style={{ width: teamLeagueInfo.useLineupStrength ? '19%' : '31%' }}>Phone</th>
						<th style={{ width: '10%' }}>Payment</th>
					</tr>
				</thead>
				<tbody>
					{sortedPlayers.length === 0 ? (
						<tr>
							<td colSpan={teamLeagueInfo.useLineupStrength ? 8 : 7} className="text-center text-gray-500 italic">
								No players found
							</td>
						</tr>
					) : (
						sortedPlayers.map((player, index) => (
							<tr key={`${player.uuid}-${index}`}>
								<td className="text-center font-medium">{index + 1}</td>
								<td className="font-medium">{player.fullname}</td>
								<td>
									<div className="text-left">
										<div className="text-xs font-medium ">{formatRating(player.ratingSingles, player.ratingDoubles).doubles}</div>
										<div className="text-xs text-gray-500">
											{formatRating(player.ratingSingles, player.ratingDoubles).singles}
										</div>
									</div>
								</td>
								<td>{getRoleLabel(player.status)}</td>
								<td>{getStatusLabel(getInjuredStatus(player))}</td>
								{teamLeagueInfo.useLineupStrength ? <td>{player.lineupStrength || '0'}</td> : null}
								<td>
									{formatPhoneFromParts({
										internationalCountryCallingCode: player.internationalCallingCode ?? '',
										phone: player.phone ?? ''
									})}
								</td>
								<td>{formatPayment(player.payment)}</td>
							</tr>
						))
					)}
				</tbody>
			</table>

			{sortedPlayers.length > 0 && (
				<div className="mt-4 text-sm text-gray-600">
					<p>Total players: {sortedPlayers.length}</p>
				</div>
			)}
		</div>
	);
});
